import React, { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Bell, Check, Clock, X } from 'lucide-react';
import { useWebSocket } from '../services/useWebSocket';
import { useReminderStore } from '../services/reminderStore';

interface ReminderDueEvent {
  id?: string;
  title: string;
  body?: string;
  link?: string;
}

export const ReminderAlerts: React.FC = () => {
  const { on } = useWebSocket();
  const syncReminders = useReminderStore((s) => s.syncReminders);
  const updateReminder = useReminderStore((s) => s.updateReminder);
  const snoozeReminder = useReminderStore((s) => s.snoozeReminder);
  const dismissReminder = useReminderStore((s) => s.dismissReminder);
  const shownRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    return on('reminder:due', (event) => {
      const data = (event.data ?? {}) as ReminderDueEvent;
      const title = data.title || 'Reminder';
      const body = data.body;
      const id = data.id;

      if (id && shownRef.current.has(id)) return;
      if (id) shownRef.current.add(id);

      // Refresh the local store so the banner and lists reflect the due reminder.
      void syncReminders();

      toast.custom(
        (t) => (
          <div className="min-w-[280px] max-w-[420px] rounded-xl border border-zinc-200 bg-white p-4 shadow-lg dark:border-zinc-700 dark:bg-zinc-900">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                <Bell className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{title}</p>
                {body && (
                  <p className="mt-0.5 text-xs text-zinc-600 line-clamp-3 dark:text-zinc-400">{body}</p>
                )}
              </div>
            </div>
            <div className="mt-3 flex items-center justify-end gap-2">
              {id ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      void updateReminder(id, { status: 'done' });
                      toast.dismiss(t);
                    }}
                    className="inline-flex items-center gap-1 rounded-md bg-zinc-900 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-200"
                  >
                    <Check className="h-3 w-3" /> Done
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const until = new Date(Date.now() + 10 * 60 * 1000).toISOString();
                      void snoozeReminder(id, until);
                      toast.dismiss(t);
                    }}
                    className="inline-flex items-center gap-1 rounded-md border border-zinc-200 bg-white px-2.5 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
                  >
                    <Clock className="h-3 w-3" /> Snooze
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      void dismissReminder(id);
                      toast.dismiss(t);
                    }}
                    className="inline-flex items-center gap-1 rounded-md border border-zinc-200 bg-white px-2.5 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
                  >
                    <X className="h-3 w-3" /> Dismiss
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => toast.dismiss(t)}
                  className="rounded-md border border-zinc-200 bg-white px-2.5 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
                >
                  OK
                </button>
              )}
            </div>
          </div>
        ),
        { duration: 20_000 }
      );
    });
  }, [on, syncReminders, updateReminder, snoozeReminder, dismissReminder]);

  return null;
};

export default ReminderAlerts;
